import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  uploadInvoice,
  uploadInvoiceReset,
  fetchUploadedInvoices,
  fetchAnonymousQuota,
} from '../../actions/invoiceUpload.action';
import {
  PageWrapper,
  PageTitle,
  PageIntro,
  IntroHighlight,
  IntroHero,
  CompactPromoBanner,
  SectionTitle,
  UploadSection,
  FulfillmentChoice,
  FulfillmentChoiceLabel,
  FulfillmentChoiceHint,
  FulfillmentOptions,
  FulfillmentChoiceOption,
  FieldError,
  DropZone,
  DropZoneIcon,
  DropZoneLabel,
  ChecklistBox,
  ChecklistList,
  DropZoneFormats,
  TermsRow,
  TermsExplanation,
  FileList,
  FileChip,
  UploadButton,
  UploadButtonCaption,
  UploadingOverlay,
  ErrorMessage,
  InvoiceTable,
  Pagination,
  EmptyState,
  LoadingWrapper,
  ViewLink,
  QuotaBanner,
  PromptCard,
  PromptCardHeader,
  PromptCardTitle,
  PromptCardText,
  PromptCardButton,
  PromptCardDivider,
  PromptCardFooter,
  ExpandedVideo,
  VideoFrame,
} from './InvoiceUploadPage.style';
import Loading from '../../components/Loading/Loading';
import PopupModal from '../../components/PopupModal/PopupModal';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {
  ANALYTICS_EVENTS,
  INVOICE_UPLOAD_ACCEPTED_TYPES,
  INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_TITLE,
  INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_BODY,
  JJ_LOCAL_STORAGE,
  SERVICE_TYPE,
  CONFIRMATION_PRICE_COMPARE_BANNER,
  PRICE_COMPARE_HELP_VIDEO,
  MESSAGES,
} from '../../constants/constants';
import {LOGIN_PATH, JOIN_PATH} from '../../constants/routePaths';
import {formatDate, validateFile} from '../../helpers/invoiceUpload.helper';
import {persistDataToLocalStorage} from '../../helpers/localStorage.helper';
import {
  TERMS_AND_CONDITIONS_LINK,
  PRIVACY_POLICY_LINK,
} from '../../constants/links';

const supportsSmoothScroll =
  typeof document !== 'undefined' &&
  'scrollBehavior' in document.documentElement.style;

// iOS Safari has a long-standing bug where scrollIntoView called synchronously
// from a click handler — right as the error text above it renders and shifts
// the layout — silently no-ops instead of scrolling. Deferring a frame lets
// that layout settle first. Safari (pre-15.4) also ignores `behavior:
// 'smooth'` entirely, so fall back to an instant jump there rather than not
// scrolling at all.
const scrollElementIntoView = element => {
  if (!element) return;
  requestAnimationFrame(() => {
    element.scrollIntoView(
      supportsSmoothScroll
        ? {behavior: 'smooth', block: 'start'}
        : {block: 'start'}
    );
  });
};

const InvoiceUploadPage = ({history}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const fulfillmentSectionRef = useRef(null);
  const {auth, invoiceUpload} = useSelector(state => ({
    auth: state.auth,
    invoiceUpload: state.invoiceUpload || {},
  }));

  const isAnonymous = !auth;

  const uploading = invoiceUpload.uploading || false;
  const uploadError = invoiceUpload.uploadError || null;
  const uploadResult = invoiceUpload.uploadResult || null;
  const invoices = invoiceUpload.invoices || [];
  const invoicesLoading = invoiceUpload.invoicesLoading || false;
  const invoicesError = invoiceUpload.invoicesError || null;
  const page = invoiceUpload.page || {page: 0, totalPages: 0, totalElements: 0};
  // Anonymous-only — see anonymous-price-comparison-api.pdf.
  const quota = invoiceUpload.quota || null;
  const featureUnavailable = invoiceUpload.featureUnavailable || false;

  const processId = uploadResult && uploadResult.processId;

  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);
  // No default — the visitor must actively pick one (see handleUpload), rather
  // than silently comparing against a delivery price they never asked for.
  const [fulfillmentType, setFulfillmentType] = useState(null);
  const [fulfillmentTypeError, setFulfillmentTypeError] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  // The public upload API counts failed attempts against the same 5-a-day
  // allowance as successful ones, so anonymous visitors must change their
  // file selection (a genuinely new attempt) rather than immediately
  // re-submitting the same failed one and burning through their quota.
  const [uploadLocked, setUploadLocked] = useState(false);

  useEffect(() => {
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.COMPARE_PRICES_PAGE_VIEW,
      {category: 'Compare Prices'}
    );
  }, []);

  useEffect(() => {
    if (auth) {
      dispatch(fetchUploadedInvoices(0));
    } else {
      // No list endpoint for anonymous visitors — check the allowance
      // instead, before showing the form.
      dispatch(fetchAnonymousQuota());
    }
  }, [auth]);

  useEffect(() => {
    if (uploadResult) {
      setFiles([]);
      setFulfillmentType(null);
      setFulfillmentTypeError(null);
      setTermsAgreed(false);
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD_SUCCESS,
        {category: 'Compare Prices'}
      );
    }
  }, [uploadResult]);

  // Move the user straight to the results page once the file is uploaded — the
  // results page shows staged progress (reading -> matching -> savings) so the
  // wait feels like forward motion rather than a stall on this screen. Reset
  // first so returning here later doesn't redirect again.
  useEffect(() => {
    if (processId) {
      // There's no list endpoint for anonymous visitors, so this is their
      // only way back to an in-progress comparison (e.g. after a refresh).
      if (isAnonymous) {
        persistDataToLocalStorage(
          JJ_LOCAL_STORAGE.ANONYMOUS_INVOICE_PROCESS_ID,
          processId
        );
      }
      dispatch(uploadInvoiceReset());
      history.push(`/price-comparison/${processId}`);
    }
  }, [processId]);

  useEffect(() => {
    if (uploadError) {
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD_FAILED,
        {category: 'Compare Prices', error: uploadError}
      );
      if (isAnonymous) {
        setUploadLocked(true);
      }
    }
  }, [uploadError, isAnonymous]);

  const handleFiles = useCallback(newFiles => {
    const errors = [];
    const valid = [];
    Array.from(newFiles).forEach(f => {
      const err = validateFile(f);
      if (err) {
        errors.push(err);
      } else {
        valid.push(f);
      }
    });
    setFileErrors(errors);
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
      setUploadLocked(false);
      dispatch(uploadInvoiceReset());
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback(
    e => {
      handleFiles(e.target.files);
      e.target.value = '';
    },
    [handleFiles]
  );

  const removeFile = useCallback(index => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setUploadLocked(false);
  }, []);

  const handleUpload = useCallback(() => {
    setFulfillmentTypeError(null);
    if (!fulfillmentType) {
      setFulfillmentTypeError(MESSAGES.FULFILLMENT_TYPE_REQUIRED);
      // On mobile the upload button sits well below this section, so the new
      // error can land off-screen — bring the section it belongs to into view
      // rather than leaving the visitor to scroll up and hunt for it.
      scrollElementIntoView(fulfillmentSectionRef.current);
      return;
    }
    if (
      files.length > 0 &&
      termsAgreed &&
      !uploading &&
      !(isAnonymous && uploadLocked)
    ) {
      try {
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD,
          {
            category: 'Compare Prices',
            fileCount: files.length,
            fulfillmentType,
          }
        );
      } catch (e) {}

      dispatch(uploadInvoice(files, fulfillmentType));
    }
  }, [
    files,
    uploading,
    fulfillmentType,
    termsAgreed,
    isAnonymous,
    uploadLocked,
  ]);

  const handleRowClick = useCallback(id => {
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.COMPARE_PRICES_VIEW_DETAIL,
      {category: 'Compare Prices', invoiceId: id}
    );
    history.push(`/price-comparison/${id}`);
  }, []);

  const handlePageChange = useCallback(newPage => {
    dispatch(fetchUploadedInvoices(newPage));
  }, []);

  const dismissUploadError = useCallback(() => {
    dispatch(uploadInvoiceReset());
  }, [dispatch]);

  // Server-side killswitch for the anonymous flow (empty-body 503) — logged-in
  // uploads go through a different endpoint entirely, so this never applies
  // to them.
  if (isAnonymous && featureUnavailable) {
    return (
      <PageWrapper>
        <PageTitle>JJ Price Compare AI</PageTitle>
        <EmptyState>
          Price comparison is temporarily unavailable. Please try again later.
        </EmptyState>
      </PageWrapper>
    );
  }

  const renderUploadForm = (
    <UploadSection>
      {uploading && (
        <UploadingOverlay>
          <Loading isLight={false} />
          <p>Uploading your invoice&hellip;</p>
          <span>Just a moment &mdash; then we&apos;ll find your savings.</span>
        </UploadingOverlay>
      )}
      <SectionTitle>Upload an invoice</SectionTitle>

      <FulfillmentChoice
        ref={fulfillmentSectionRef}
        role="radiogroup"
        aria-labelledby="fulfillment-type-label"
      >
        <FulfillmentChoiceLabel id="fulfillment-type-label">
          Was this a delivery or a collection order?
        </FulfillmentChoiceLabel>
        <FulfillmentChoiceHint>
          This lets us compare against the right JJ price &mdash; we price
          delivery and collection differently, so it keeps the comparison fair.
        </FulfillmentChoiceHint>
        <FulfillmentOptions>
          <FulfillmentChoiceOption
            $selected={fulfillmentType === SERVICE_TYPE.DELIVERY}
          >
            <input
              type="radio"
              name="fulfillmentType"
              value={SERVICE_TYPE.DELIVERY}
              checked={fulfillmentType === SERVICE_TYPE.DELIVERY}
              onChange={() => {
                setFulfillmentType(SERVICE_TYPE.DELIVERY);
                setFulfillmentTypeError(null);
              }}
            />
            <div>
              <strong>Delivery</strong>
              <span>My supplier delivers to me</span>
            </div>
          </FulfillmentChoiceOption>
          <FulfillmentChoiceOption
            $selected={fulfillmentType === SERVICE_TYPE.COLLECTION}
          >
            <input
              type="radio"
              name="fulfillmentType"
              value={SERVICE_TYPE.COLLECTION}
              checked={fulfillmentType === SERVICE_TYPE.COLLECTION}
              onChange={() => {
                setFulfillmentType(SERVICE_TYPE.COLLECTION);
                setFulfillmentTypeError(null);
              }}
            />
            <div>
              <strong>Collection</strong>
              <span>I collect from my supplier / cash &amp; carry</span>
            </div>
          </FulfillmentChoiceOption>
        </FulfillmentOptions>
        {fulfillmentTypeError && (
          <FieldError>{fulfillmentTypeError}</FieldError>
        )}
      </FulfillmentChoice>

      <ChecklistBox>
        <ChecklistList>
          <li>
            One invoice at a time &mdash; add more to build a fuller comparison
          </li>
          <li>
            Trade supplier invoices only (not till receipts or retail orders)
          </li>
          <li>
            Multi-page invoice? Include every page, but only from the same
            invoice
          </li>
          <li>
            Photos welcome &mdash; just make sure prices and product names are
            clearly readable
          </li>
          <li>Make sure the edges of the document are in the picture</li>
        </ChecklistList>
      </ChecklistBox>

      <DropZone
        $isDragOver={isDragOver}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
      >
        <DropZoneIcon viewBox="0 0 24 24">
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M12 12v6M9.5 14.5 12 12l2.5 2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </DropZoneIcon>
        <DropZoneLabel>Click here to upload your invoice</DropZoneLabel>
        <DropZoneFormats>
          Supported formats: PDF, JPEG, PNG, WebP
        </DropZoneFormats>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={INVOICE_UPLOAD_ACCEPTED_TYPES.join(',')}
          onChange={handleFileInput}
          style={{display: 'none'}}
        />
      </DropZone>

      {files.length > 0 && (
        <FileList>
          {files.map((file, i) => (
            <FileChip key={`${file.name}-${i}`}>
              {file.name}
              <button onClick={() => removeFile(i)}>&times;</button>
            </FileChip>
          ))}
        </FileList>
      )}

      <TermsRow>
        <label>
          <input
            type="checkbox"
            checked={termsAgreed}
            onChange={e => setTermsAgreed(e.target.checked)}
          />
          <span>
            I&apos;ve read and agree to the{' '}
            <a
              href={TERMS_AND_CONDITIONS_LINK.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {TERMS_AND_CONDITIONS_LINK.content}
            </a>{' '}
            and{' '}
            <a
              href={PRIVACY_POLICY_LINK.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
        <TermsExplanation>
          We use your invoice to build your comparison and, in line with the
          Privacy Policy, to improve our pricing and range. We don&apos;t share
          your prices with anyone else in a form that identifies your business.
        </TermsExplanation>
      </TermsRow>

      <UploadButton
        onClick={handleUpload}
        disabled={
          files.length === 0 ||
          !termsAgreed ||
          uploading ||
          (isAnonymous && uploadLocked)
        }
      >
        {uploading ? 'Uploading...' : 'Compare my prices'}
      </UploadButton>

      {files.length === 0 && (
        <UploadButtonCaption>
          Add at least one invoice to continue.
        </UploadButtonCaption>
      )}
      {files.length > 0 && !termsAgreed && (
        <UploadButtonCaption>
          Please agree to the terms to continue.
        </UploadButtonCaption>
      )}
      {files.length > 0 && isAnonymous && uploadLocked && (
        <UploadButtonCaption>
          Remove or add a file to try again.
        </UploadButtonCaption>
      )}

      {fileErrors.length > 0 && (
        <ErrorMessage>
          {fileErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </ErrorMessage>
      )}

      {!isAnonymous && uploadError && (
        <ErrorMessage>{uploadError}</ErrorMessage>
      )}
    </UploadSection>
  );

  // No list endpoint for anonymous visitors, so there's no history to show —
  // the quota banner above the form already tells them how many they've used.
  const renderInvoiceHistory = !isAnonymous && (
    <>
      <SectionTitle>Your uploaded invoices</SectionTitle>

      {invoicesLoading && (
        <LoadingWrapper>
          <Loading isLight={false} />
        </LoadingWrapper>
      )}

      {invoicesError && <ErrorMessage>{invoicesError}</ErrorMessage>}

      {!invoicesLoading && !invoicesError && invoices.length === 0 && (
        <EmptyState>
          No invoices uploaded yet. Add your first invoice above to see how much
          you could save with JJ.
        </EmptyState>
      )}

      {!invoicesLoading && invoices.length > 0 && (
        <>
          <InvoiceTable>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Invoice No.</th>
                <th>Invoice Date</th>
                <th>Uploaded</th>
                <th>Savings</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} onClick={() => handleRowClick(inv.id)}>
                  <td>{inv.supplierName || '-'}</td>
                  <td>{inv.invoiceNumber || '-'}</td>
                  <td>{inv.invoiceDate ? formatDate(inv.invoiceDate) : '-'}</td>
                  <td>{inv.uploadedAt ? formatDate(inv.uploadedAt) : '-'}</td>
                  <td>See details</td>
                  <td>
                    <ViewLink>View &rsaquo;</ViewLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </InvoiceTable>

          {page.totalPages > 1 && (
            <Pagination>
              <button
                disabled={page.page === 0}
                onClick={() => handlePageChange(page.page - 1)}
              >
                Previous
              </button>
              <span>
                Page {page.page + 1} of {page.totalPages}
              </span>
              <button
                disabled={page.page >= page.totalPages - 1}
                onClick={() => handlePageChange(page.page + 1)}
              >
                Next
              </button>
            </Pagination>
          )}
        </>
      )}
    </>
  );

  const showRegisterPrompt = isAnonymous && quota && quota.blocked;

  return (
    <PageWrapper>
      {isAnonymous && uploadError && (
        <PopupModal
          modalId="modal--anonymous-upload-error"
          title="We couldn't upload your invoice"
          content={uploadError}
          isError={true}
          showOKButton={true}
          callback={dismissUploadError}
          closeModalCallback={dismissUploadError}
          showModal={!!uploadError}
        />
      )}
      <IntroHero>
        <CompactPromoBanner
          desktopImage={CONFIRMATION_PRICE_COMPARE_BANNER.DESKTOP_IMAGE}
          mobileImage={CONFIRMATION_PRICE_COMPARE_BANNER.MOBILE_IMAGE}
          alt="JJ Price Compare AI"
        />
      </IntroHero>

      {showRegisterPrompt ? (
        <PromptCard>
          <PromptCardHeader>
            <div>
              <PromptCardTitle>
                {INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_TITLE}
              </PromptCardTitle>
              <PromptCardText>
                {INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_BODY}
              </PromptCardText>
            </div>
            <PromptCardButton href={JOIN_PATH}>
              Create a free account
            </PromptCardButton>
          </PromptCardHeader>
          <PromptCardDivider />
          <PromptCardFooter>
            Already have an account with us? <Link to={LOGIN_PATH}>Log in</Link>
          </PromptCardFooter>
        </PromptCard>
      ) : (
        renderUploadForm
      )}
      <ExpandedVideo>
        <VideoFrame>
          <iframe
            src={PRICE_COMPARE_HELP_VIDEO.EMBED_URL}
            title="How JJ Price Compare AI works"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen={true}
          />
        </VideoFrame>
      </ExpandedVideo>
      {renderInvoiceHistory}
    </PageWrapper>
  );
};

InvoiceUploadPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default InvoiceUploadPage;
